import { CompanySelectorView } from '../view/CompanySelectorView';
import { templateCompanySelector } from '../view/template';
import { CompanySelectorRepository } from '../repository/CompanySelectorRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../framework/BaseWebComponentController';
import { CompanySelectorWebComponent } from '..';
import { CompanySelectorWebComponentSchema } from '../types';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { SelectOption } from '../../ui/gzDropdown/types';
import { GzDropdownWebComponent } from '../../ui/gzDropdown';
import { CompanySelectorEvents } from '../types/constants';

export class CompanySelectorController extends BaseWebComponentController {
  public component: CompanySelectorWebComponent;
  public dataRepository: CompanySelectorRepository;
  public view: CompanySelectorView;
  public viewConstructor: typeof CompanySelectorView;
  public dataRepositoryConstructor: typeof CompanySelectorRepository;

  //TODO(@tmengazi,@ivlasceanu):  Have this bounded in size. See: https://jira.bitdefender.biz/browse/CERES-15346
  private searchStringToAvailableCompanies: Map<string, Array<Target>>;

  private static getCompanySelectorOptions(availableCompanies: Array<Target> | undefined): Array<SelectOption> {
    return availableCompanies
      ? availableCompanies.map(company => {
          return {
            title: company.companyName,
            value: company.companyId,
          };
        })
      : [];
  }

  constructor(component: CompanySelectorWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: CompanySelectorView,
      viewTemplate: templateCompanySelector,
      dataRepositoryConstructor: CompanySelectorRepository,
    });

    this.searchStringToAvailableCompanies = new Map<string, Array<Target>>();

    // This is the initial state of the selector, corresponding to empty search string. Put it in the cache.
    if (component.hasAvailableCompanies()) {
      this.searchStringToAvailableCompanies.set('', component.availableCompanies);
    }
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$companySelectorDropdown,
        event: CompanySelectorEvents.SELECTION_CHANGED,
        callback: this.onSelectedCompanyChanged,
      },
      {
        element: this.view.dom.$companySelectorDropdown,
        event: CompanySelectorEvents.ON_NEW_INPUT_STRING,
        callback: this.onNewInputString,
      },
      {
        element: this.component,
        event: CompanySelectorEvents.AVAILABLE_COMPANIES_UPDATED,
        callback: this.updateSearchStringTargetsMapping,
      },
    ];
  }

  private async onSelectedCompanyChanged(ev: WebComponentEvent) {
    const selectedOption = ev.detail;
    await this.selectCompany(selectedOption.value, selectedOption.title);
  }

  private async selectCompany(value: string, title: string) {
    // Save the previous active company, so we can reset it in the dropdown if we get an error.
    const previousTarget = this.component.selectedTarget;
    const currentTarget = {
      companyId: value,
      companyName: title,
    };

    if (currentTarget.companyId === this.component.selectedTarget.companyId) {
      return false;
    }

    this.component.dispatchEvent(new CustomEvent(CompanySelectorEvents.SELECT_COMPANY,
      { detail: { currentTarget: currentTarget }, bubbles: true, cancelable: true, composed: false }));

    try {
      this.component.selectedTarget = currentTarget;

      const companyClickedHandler = this.component.onCompanyClicked;
      if (companyClickedHandler) {
        await companyClickedHandler(currentTarget);
      }

      const companyChangedWithSuccessHandler = this.component.onCompanyChangedWithSuccess;
      if (companyChangedWithSuccessHandler) {
        await companyChangedWithSuccessHandler();
      }
    } catch (error) {
      console.error(error);

      // Revert the previously selected company.
      this.component.selectedTarget = previousTarget;
      const lastSelected = {
        title: previousTarget.companyName,
        value: previousTarget.companyId,
      };
      const selector = this.view.dom.$companySelectorDropdown;
      selector.selectedOption = lastSelected;
      selector.value = lastSelected.value;
      selector.setLastSelectedOption();

      const companyChangedWithErrorHandler = this.component.onCompanyChangedWithError;
      if (companyChangedWithErrorHandler) {
        await companyChangedWithErrorHandler(error);
      }

      // Show an error dialog.
      this.view.showErrorDialog(currentTarget.companyName);
    }

    return false;
  }

  public async selectActiveCompany() {
    const activeCompany = this.dependencies.getActiveCompany();
    if (activeCompany) {
      await this.selectCompany(activeCompany.companyId, activeCompany.companyName);
    }
  }

  private async onNewInputString(ev: WebComponentEvent) {
    const selector = this.view.dom.$companySelectorDropdown as GzDropdownWebComponent;
    selector.isError = false;

    try {
      const { searchString } = ev.detail;

      const availableCompanies = await this.getAvailableCompanies(searchString);
      this.component.searchString = searchString;
      this.component.availableCompanies = availableCompanies;

      selector.options = CompanySelectorController.getCompanySelectorOptions(availableCompanies);
    } catch (e) {
      console.error(e);
      selector.isError = true;
      selector.options = [];
    }

    return false;
  }

  private async getAvailableCompanies(searchString: string): Promise<Array<Target>> {
    if (!this.searchStringToAvailableCompanies.has(searchString)) {
      const selector = this.view.dom.$companySelectorDropdown;
      selector.isLoading = true;
      try {
        const data = await this.dataRepository.getCompanies({
          companyName: searchString,
          ...this.component.companySelectorParams,
        });
        this.searchStringToAvailableCompanies.set(searchString, data && data.targets ? data.targets : []);
      } finally {
        selector.isLoading = false;
      }
    }

    return this.searchStringToAvailableCompanies.get(searchString) || [];
  }

  async getDataForView(): Promise<CompanySelectorWebComponentSchema> {
    return {
      disabled: this.component.disabled,
      selectedTarget: this.component.selectedTarget,
      availableCompanies: this.component.availableCompanies,
      label: this.component.label,
      placeholder: this.component.placeholder,
    };
  }

  public updateSearchStringTargetsMapping(event: WebComponentEvent): void {
    const targets = event.detail.availableCompanies as Array<Target>;
    const searchString = event.detail.searchString;
    if (searchString === '') {
      this.searchStringToAvailableCompanies.set('', targets);
    }
  }
}
